class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = Array.from(this.querySelectorAll('li')).filter((li) => !li.hidden);
    if (!allElements.length) return;

    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      const idx = allElements.indexOf(selectedElement);
      activeElement = allElements[idx + 1] || allElements[0];
    } else if (moveUp) {
      const idx = allElements.indexOf(selectedElement);
      activeElement = allElements[idx - 1] || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  /* ---------- Fetching ---------- */

  fetchProductMarkup(searchTerm) {
    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
        const section = parsedHTML.querySelector('#shopify-section-predictive-search');
        return section ? section.innerHTML : '';
      })
      .catch((error) => {
        console.error('Predictive search error:', error);
        return '';
      });
  }

  fetchCollectionProducts(searchTerm) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const url = `${root}search/suggest.json?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=collection&${encodeURIComponent('resources[limit]')}=2`;

    return fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const collections =
          json && json.resources && json.resources.results && json.resources.results.collections
            ? json.resources.results.collections
            : [];

        if (!collections.length) return [];

        return Promise.all(
          collections.map((c) =>
            fetch(`${root}collections/${c.handle}/products.json?limit=10`)
              .then((r) => (r.ok ? r.json() : { products: [] }))
              .then((d) => d.products || [])
              .catch(() => [])
          )
        ).then((lists) => {
          const seen = {};
          const merged = [];
          lists.forEach((list) =>
            list.forEach((p) => {
              if (!seen[p.handle]) {
                seen[p.handle] = true;
                merged.push(p);
              }
            })
          );
          return merged;
        });
      })
      .catch(() => []);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase().replace(/\s+/g, ' ').trim();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey] !== undefined) {
      const cached = this.cachedResults[queryKey];
      this.renderSearchResults(cached.markup, searchTerm, cached.collectionProducts);
      return;
    }

    Promise.all([
      this.fetchProductMarkup(searchTerm),
      this.fetchCollectionProducts(searchTerm)
    ]).then(([markup, collectionProducts]) => {
      this.cachedResults[queryKey] = { markup, collectionProducts };

      // Tab tak user ne aur type kar diya to purana result render na karo
      if (this.getQuery().toLowerCase().replace(/\s+/g, ' ').trim() !== queryKey) return;

      this.renderSearchResults(markup, searchTerm, collectionProducts);
    });
  }

  /* ---------- Live region ---------- */

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  /* ---------- Rendering ---------- */

  getResultsBox() {
    return (
      this.predictiveSearchResults.querySelector('#predictive-search-results') ||
      this.predictiveSearchResults.querySelector('.predictive-search-results')
    );
  }

  escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // products.json ki price "325.00" jaisi string hoti hai
  formatPrice(price) {
    const cents = Math.round(parseFloat(price) * 100);
    if (isNaN(cents)) return '';
    if (window.theme && theme.Currency && theme.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  // Bilkul aapke product <li> jaisa markup
  buildCollectionItem(product, index) {
    const variant = product.variants && product.variants[0] ? product.variants[0] : null;
    const price = variant ? this.formatPrice(variant.price) : '';
    const comparePrice =
      variant && variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price)
        ? this.formatPrice(variant.compare_at_price)
        : '';

    let img = product.images && product.images[0] ? product.images[0].src : '';
    if (img) img = img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=1000';

    const title = this.escapeHtml(product.title);
    const handle = this.escapeHtml(product.handle);

    return `<li id="predictive-search-option-c${index}" class="predictive-search__list-item" role="option" aria-selected="false" data-from-collection="true">
          <a href="/products/${handle}" class="predictive-search__item predictive-search__item--link  link--text" tabindex="-1">${img ? `<img class="predictive-search__image w-100" src="${this.escapeHtml(img)}" alt="${title}" width="50" height="75">` : ''}<div class="predictive-search__item-content"><h3 class="predictive-search__item-heading product-card__name">${title}</h3>
<div class="mt-1 price">
  <div class="price__container"><div class="price__regular">
      <span class="visually-hidden visually-hidden--inline">Regular price</span>
      <span class="price-item price-item--regular">
        ${price}
      </span>
    </div>
    <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span>
          <s class="price-item price-item--regular small">
            ${comparePrice}
          </s>
        </span><span class="visually-hidden visually-hidden--inline">Sale price</span>
      <span class="price-item price-item--sale price-item--last">
        ${price}
      </span>
    </div>
  </div></div>
 </div>
          </a>
        </li>`;
  }

  renderSearchResults(resultsMarkup, searchTerm = '', collectionProducts = []) {
    this.predictiveSearchResults.innerHTML = resultsMarkup || '';

    const normalize = (text) =>
      text.toLowerCase().replace(/\s+/g, ' ').trim();

    const query = normalize(searchTerm);
    const words = query.split(' ').filter(Boolean);

    const resultsBox = this.getResultsBox();
    let resultsList = this.predictiveSearchResults.querySelector('#predictive-search-results-list');

    // Server se list nahi aayi par collection products hain => nayi list banao
    if (!resultsList && collectionProducts.length) {
      const ul = document.createElement('ul');
      ul.id = 'predictive-search-results-list';
      ul.setAttribute('role', 'listbox');
      ul.className = 'predictive-search__results-list list-unstyled';
      (resultsBox || this.predictiveSearchResults).appendChild(ul);
      resultsList = ul;
    }

    // Kuch bhi nahi mila => No result
    if (!resultsList) {
      if (resultsBox) resultsBox.style.display = 'none';
      this.showNoResult();
      this.setAttribute('results', true);
      this.removeAttribute('loading');
      this.open();
      return;
    }

    const items = Array.from(resultsList.querySelectorAll(':scope > li'));
    const seeResultsButton = resultsList.querySelector('.okok123');

    let matchCount = 0;
    const exactItems = [];
    const existingHandles = {};

    // 1) Product title match (query ka har word title mein)
    items.forEach((item) => {
      const titleElement = item.querySelector('.predictive-search__item-heading');
      const title = titleElement ? normalize(titleElement.textContent) : '';
      const isMatch = words.length > 0 && words.every((w) => title.includes(w));

      item.hidden = !isMatch;

      if (isMatch) {
        matchCount++;
        if (title === query) exactItems.push(item);

        const a = item.querySelector('a[href*="/products/"]');
        if (a) {
          const h = a.getAttribute('href').split('/products/')[1].split(/[?#]/)[0];
          existingHandles[h] = true;
        }
      }
    });

    // 2) Collection naam se mile products (title filter nahi lagta)
    const newCollectionItems = collectionProducts.filter((p) => !existingHandles[p.handle]);
    if (newCollectionItems.length) {
      const html = newCollectionItems.map((p, i) => this.buildCollectionItem(p, i)).join('');
      if (seeResultsButton) {
        seeResultsButton.insertAdjacentHTML('beforebegin', html);
      } else {
        resultsList.insertAdjacentHTML('beforeend', html);
      }
      matchCount += newCollectionItems.length;
    }

    // Exact match sabse upar
    exactItems.reverse().forEach((item) => resultsList.prepend(item));

    if (matchCount > 0) {
      if (resultsBox) resultsBox.style.display = '';
      if (seeResultsButton) seeResultsButton.hidden = false;
    } else {
      if (resultsBox) resultsBox.style.display = 'none';
      if (seeResultsButton) seeResultsButton.hidden = true;
      this.showNoResult();
    }

    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  showNoResult() {
    const msg =
      (window.theme && theme.strings && theme.strings.ajaxSearchNoResult) ||
      'No results found';

    const el = document.createElement('p');
    el.className = 'predictive-search__no-result text-center py-3 m-0';
    el.textContent = msg;
    this.predictiveSearchResults.appendChild(el);
  }

  /* ---------- Open / Close ---------- */

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);